<script setup>
import { computed } from 'vue'
import { dateParts, formatDate, formatDay, formatDayMonth } from '@/lib/dates'

/**
 * A period written the way it reads: what the two ends have in common is written once, on the
 * right.
 *
 *   same month    1 → 31 Ott 2026
 *   same year     10 Ott → 31 Nov 2026
 *   different     10 Ott 2026 → 10 Nov 2027
 *
 * The dates are read in the timezone of the visitor, like the rest of the site (see lib/dates).
 */
const props = defineProps({
  /** ISO instant the period opens at. */
  from: { type: String, default: null },
  /** ISO instant the period closes at. */
  to: { type: String, default: null },
})

const text = computed(() => {
  if (!props.from || !props.to) {
    return `${formatDate(props.from)} → ${formatDate(props.to)}`
  }

  const from = dateParts(props.from)
  const to = dateParts(props.to)

  if (from.year !== to.year) {
    return `${formatDate(props.from)} → ${formatDate(props.to)}`
  }

  if (from.month !== to.month) {
    return `${formatDayMonth(props.from)} → ${formatDate(props.to)}`
  }

  // The same day is one date, not a period of one.
  return from.day === to.day
    ? formatDate(props.from)
    : `${formatDay(props.from)} → ${formatDate(props.to)}`
})
</script>

<template>
  <span>{{ text }}</span>
</template>
